// src/pages/Questionnaires.tsx
import React from "react";
import AppShell from "../layout/AppShell";
import { listQuestionsPage, type Page } from "../services/questionsService";
import { adminUpsertQuestion } from "../services/adminApi";
import type { Question, QuestionPatch } from "../models/question";
import LoadingSpinner from "../components/LoadingSpinner";
import { MdQuiz, MdEdit, MdRefresh, MdSave, MdClose } from "react-icons/md";
import { QUESTIONNAIRES_ROLES } from "../models/auth";
import { useAuthStore } from "../stores/authStore";

/** Dimensiones disponibles (ajústalas a tus docs) */
const DIMENSIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "ambiental", label: "Ambiental" },
  { key: "social", label: "Social" },
  { key: "gobernanza", label: "Gobernanza" },
  { key: "economica", label: "Económica" },
];

/** Cada dimensión tiene 10 variables (1..10) */
const VARS_PER_DIM = 10 as const;

export default function Questionnaires(): React.ReactElement {
  const [dimension, setDimension] = React.useState<string>(
    DIMENSIONS[0]?.key ?? ""
  );
  const [variable, setVariable] = React.useState<number | "">("");

  const [rows, setRows] = React.useState<Question[]>([]);
  const [cursor, setCursor] = React.useState<Page<Question>["next"]>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [err, setErr] = React.useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const [editing, setEditing] = React.useState<Question | null>(null);

  const canEdit = useAuthStore((s) => s.hasAnyRole(QUESTIONNAIRES_ROLES));

  const validVar =
    typeof variable === "number" && variable >= 1 && variable <= VARS_PER_DIM;
  const canSearch = Boolean(dimension) && validVar;

  const dimensionLabel = React.useMemo(
    () => DIMENSIONS.find((d) => d.key === dimension)?.label ?? dimension,
    [dimension]
  );

  const load = React.useCallback(
    async (reset = false) => {
      if (!canSearch) return;
      setLoading(true);
      try {
        const page = await listQuestionsPage({
          dimensionKey: dimension,
          variableIndex: variable as number,
          cursor: reset ? null : cursor,
          limit: 30,
        });
        setRows((r) => (reset ? page.items : [...r, ...page.items]));
        setCursor(page.next);
        setErr(null);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [canSearch, dimension, variable, cursor]
  );

  React.useEffect(() => {
    if (validVar) void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimension, variable]);

  const openEdit = (q: Question) => {
    if (!canEdit) return;
    setEditing(q);
    setDrawerOpen(true);
  };

  return (
    <AppShell>
      <section className="card p-4">
        {/* Toolbar */}
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 md:items-center">
          <div className="flex items-center gap-2">
            <span className="icon-accent text-2xl">
              <MdQuiz />
            </span>
            <h2 className="text-lg font-semibold">Cuestionarios</h2>
            {validVar && (
              <span className="badge-tonal ml-2">
                {dimensionLabel} • Var {String(variable).padStart(2, "0")}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            <select
              className="input-surface w-full sm:w-44"
              value={dimension}
              onChange={(e) => setDimension(e.target.value)}
            >
              {DIMENSIONS.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </select>

            <select
              className="input-surface w-full sm:w-44"
              value={String(variable)}
              onChange={(e) => {
                const v = e.target.value;
                setVariable(v === "" ? "" : Number(v));
              }}
            >
              <option value="">Variable (1–10)…</option>
              {Array.from({ length: VARS_PER_DIM }, (_, i) => i + 1).map(
                (n) => (
                  <option key={n} value={n}>
                    {`Var ${n}`}
                  </option>
                )
              )}
            </select>

            <button
              className="btn btn-outline flex items-center gap-1"
              onClick={() => load(true)}
              disabled={loading || !canSearch}
              type="button"
            >
              <MdRefresh /> Recargar
            </button>
          </div>
        </div>

        {err && <p className="mb-3 text-sm text-red-600">{err}</p>}

        {/* Lista móvil */}
        <div className="space-y-2 md:hidden">
          {loading && rows.length === 0 ? (
            <LoadingSpinner label="Cargando preguntas…" />
          ) : rows.length > 0 ? (
            rows.map((q) => (
              <article
                key={q.id}
                className="rounded-2xl border border-surface bg-surface p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="badge-tonal">#{q.questionIndex}</span>
                  {canEdit && (
                    <button
                      className="btn btn-outline flex items-center gap-1"
                      onClick={() => openEdit(q)}
                      type="button"
                    >
                      <MdEdit /> Editar
                    </button>
                  )}
                </div>
                <p className="text-sm">{q.text}</p>
                {q.helpText ? (
                  <p className="mt-2 text-xs opacity-80">{q.helpText}</p>
                ) : null}
              </article>
            ))
          ) : (
            canSearch && (
              <div className="py-10 text-center text-on-surface/70">
                Sin resultados para esa variable.
              </div>
            )
          )}
        </div>

        {/* Tabla desktop */}
        <div className="hidden overflow-x-auto rounded-lg border border-surface md:block">
          <table className="table-tonal text-sm">
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Pregunta</Th>
                <Th>Ayuda</Th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8">
                    <LoadingSpinner label="Cargando preguntas…" />
                  </td>
                </tr>
              ) : (
                rows.map((q) => (
                  <tr key={q.id}>
                    <Td className="w-20">{q.questionIndex}</Td>
                    <Td className="max-w-[640px]">{q.text}</Td>
                    <Td className="max-w-[420px]">
                      {q.helpText && q.helpText.length ? q.helpText : "—"}
                    </Td>
                    <td className="px-3 py-2 text-right">
                      {canEdit && (
                        <button
                          className="btn btn-outline flex items-center gap-1"
                          onClick={() => openEdit(q)}
                          type="button"
                        >
                          <MdEdit /> Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}

              {!loading && rows.length === 0 && canSearch && (
                <tr>
                  <td
                    colSpan={4}
                    className="py-10 text-center text-on-surface/70"
                  >
                    Sin resultados para esa variable.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            className="btn btn-outline"
            onClick={() => load(false)}
            disabled={loading || !cursor}
            type="button"
          >
            {loading ? "Cargando…" : cursor ? "Cargar más" : "No hay más"}
          </button>
        </div>
      </section>

      <QuestionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        editing={editing}
        onSaved={(patch) => {
          const id = editing?.id ?? null;
          if (!id) return;
          setRows((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)));
        }}
      />
    </AppShell>
  );
}

/* ── Drawer (Texto + Ayuda) ───────────────────────────────────────────── */
function QuestionDrawer({
  open,
  onClose,
  editing,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  editing: Question | null;
  onSaved: (patch: Partial<Question>) => void;
}): React.ReactElement | null {
  const [text, setText] = React.useState<string>("");
  const [helpText, setHelpText] = React.useState<string>("");

  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    if (editing) {
      setText(editing.text);
      setHelpText(editing.helpText ?? "");
    } else {
      setText("");
      setHelpText("");
    }
    setError(null);
  }, [open, editing]);

  if (!open || !editing) return null;

  const close = () => {
    if (saving) return;
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    const patch: QuestionPatch = {};
    if (text.trim().length) patch.text = text.trim();
    if (helpText.trim().length) patch.helpText = helpText.trim();

    try {
      setSaving(true);
      await adminUpsertQuestion(editing.id, patch);

      const localPatch: Partial<Question> = {
        ...(patch.text !== undefined ? { text: patch.text } : {}),
        ...(patch.helpText !== undefined ? { helpText: patch.helpText } : {}),
      };
      onSaved(localPatch);
      onClose();
    } catch (e2) {
      setError((e2 as Error).message || "Error interno");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-xl overflow-y-auto bg-surface p-4 shadow-xl">
        <form onSubmit={submit} className="flex h-full flex-col">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold">Editar pregunta</h3>
            <button
              className="btn btn-outline"
              onClick={close}
              type="button"
              disabled={saving}
            >
              <MdClose />
            </button>
          </div>

          <div className="grow space-y-3">
            <Field label="Texto *">
              <textarea
                className="input-surface h-28"
                value={text}
                onChange={(e) => setText(e.currentTarget.value)}
                required
              />
            </Field>

            <Field label="Ayuda (opcional)">
              <textarea
                className="input-surface h-24"
                value={helpText}
                onChange={(e) => setHelpText(e.currentTarget.value)}
              />
            </Field>

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <div className="sticky bottom-0 mt-3 flex items-center gap-2 border-t border-surface bg-surface py-2">
            <button
              type="submit"
              className="btn btn-primary flex items-center gap-1"
              disabled={saving}
            >
              <MdSave /> {saving ? "Guardando…" : "Guardar"}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={close}
              disabled={saving}
            >
              Cancelar
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

/* ── helpers UI ───────────────────────────────────────────────────────── */
function Th({ children }: { children: React.ReactNode }): React.ReactElement {
  return <th className="px-3 py-2">{children}</th>;
}
function Td({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1">
      <label className="block text-xs text-on-surface/70">{label}</label>
      <div>{children}</div>
    </div>
  );
}
